/**
 * Zentraler fetch-Wrapper für die Web-App.
 */

import { formatValidationDetail } from "@/lib/apiErrorDetail";
import { tenantSlug } from "@/lib/tenantSlug";
import { observeApiExchange, observeApiTransportError } from "@shared/serverReachability";

/** Bekannte App-Host → API-Basis (ohne trailing slash). */
const HOST_API_BASE: Record<string, string> = {
  "dev.app.ernsthaus.de": "https://dev.api.ernsthaus.de",
  "app.ernsthaus.de": "https://api.ernsthaus.de",
  "test.app.ernsthaus.de": "https://api.ernsthaus.de",
  "dev.app.campingplatz-mainkur.de": "https://dev.api.campingplatz-mainkur.de",
  "app.campingplatz-mainkur.de": "https://api.campingplatz-mainkur.de",
  "test.app.campingplatz-mainkur.de": "https://api.campingplatz-mainkur.de",
};

export function resolveApiBase(hostname: string): string {
  const mapped = HOST_API_BASE[hostname];
  if (mapped) return mapped;

  const fromBuild = (import.meta.env.VITE_API_BASE as string | undefined)?.trim();
  if (fromBuild) return fromBuild.replace(/\/$/, "");

  // Lokale Entwicklung: relative URLs → Vite-Proxy in vite.config.ts
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "";
  }

  // test.app.<domain> → api.<domain> (gleiche Produktions-API, anderer Mandant)
  if (hostname.startsWith("test.app.")) {
    return `https://api.${hostname.slice("test.app.".length)}`;
  }

  // dev.app.<domain> → dev.api.<domain> (wie Ernsthaus; test.app.* → api.* oben)
  if (hostname.startsWith("dev.app.")) {
    return `https://dev.api.${hostname.slice("dev.app.".length)}`;
  }
  if (hostname.startsWith("app.")) {
    return `https://api.${hostname.slice("app.".length)}`;
  }

  return "";
}

const RUNTIME_API_BASE: string = (() => {
  if (typeof window === "undefined") return "";
  const fromMeta = document
    .querySelector('meta[name="eh-api-base"]')
    ?.getAttribute("content");
  if (fromMeta) return fromMeta.replace(/\/$/, "");
  return resolveApiBase(window.location.hostname);
})();

export const API_BASE = RUNTIME_API_BASE;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

function currentNextUrl(): string {
  if (typeof window === "undefined") return "/";
  return window.location.href;
}

function isElectronShell(): boolean {
  if (typeof window === "undefined") return false;
  const env = (window as { envAPI?: { isElectron?: boolean } }).envAPI;
  return env?.isElectron === true;
}

/** Genau `/login` — `/login/` und andere Pfade laden das App-Shell und lösen sonst weitere 401 aus. */
function isExactInAppLoginPath(): boolean {
  return typeof window !== "undefined" && window.location.pathname === "/login";
}

let electronLoginRedirectStarted = false;

function redirectToLogin(loginUrl?: string | null): never {
  if (isElectronShell()) {
    // Parallele 401 (Shell, Badges, /me) dürfen nicht jeweils /auth/entra/start öffnen.
    // Electron würde daraus einen Systembrowser-Tab pro Request machen und auf der
    // Seite bleiben. Einmal auf die Login-Route; der Browser startet nur über den Button.
    if (!electronLoginRedirectStarted && !isExactInAppLoginPath()) {
      electronLoginRedirectStarted = true;
      window.location.replace(`${window.location.origin}/login`);
    }
    throw new ApiError(401, "redirecting-to-login");
  }

  const fallback = `${API_BASE || ""}/auth/entra/start`;
  const base = loginUrl || fallback;
  const sep = base.includes("?") ? "&" : "?";
  const target = `${base}${sep}next=${encodeURIComponent(currentNextUrl())}`;
  window.location.href = target;
  throw new ApiError(401, "redirecting-to-login");
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  json?: unknown;
  rawBody?: BodyInit | null;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  if (ct.startsWith("text/")) {
    try {
      return await res.text();
    } catch {
      return null;
    }
  }
  return null;
}

async function doFetch<T>(method: string, path: string, opts: ApiOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers || {});
  if (!headers.has("X-Tenant-Slug")) {
    headers.set("X-Tenant-Slug", tenantSlug());
  }

  let body: BodyInit | null | undefined = opts.rawBody ?? undefined;
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  } else if (opts.body !== undefined && opts.rawBody === undefined) {
    body = opts.body as BodyInit;
  }

  const { body: _ignoredBody, json: _ignoredJson, rawBody: _ignoredRaw, ...restInit } = opts;

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      ...restInit,
      method,
      credentials: "include",
      headers,
      body,
    });
  } catch (err) {
    observeApiTransportError(err);
    const message =
      err instanceof Error &&
      (err.message === "Load failed" || err.message === "Failed to fetch")
        ? "Verbindung zum Server fehlgeschlagen. Bitte Netzwerk und Anmeldung prüfen."
        : err instanceof Error
          ? err.message
          : "Netzwerkfehler";
    throw new ApiError(0, message);
  }

  observeApiExchange(res.status);

  if (res.status === 401) {
    const parsed = (await parseBody(res)) as { login_url?: string } | null;
    redirectToLogin(parsed?.login_url);
  }

  if (res.status === 412) {
    const parsed = (await parseBody(res)) as
      | { detail?: { reason?: string; login_url?: string } | string }
      | null;
    const detail = parsed && typeof parsed.detail === "object" ? parsed.detail : null;
    if (detail && detail.reason === "reauth_required") {
      redirectToLogin(detail.login_url);
    }
  }

  if (!res.ok) {
    const parsed = await parseBody(res);
    let message = `${res.status} ${res.statusText}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      message = formatValidationDetail((parsed as { detail?: unknown }).detail);
    }
    throw new ApiError(res.status, message, parsed);
  }

  if (res.status === 204) return undefined as T;
  const parsed = await parseBody(res);
  return parsed as T;
}

export const api = {
  get: <T = unknown>(path: string, opts?: ApiOptions) => doFetch<T>("GET", path, opts),
  post: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("POST", path, { ...opts, json }),
  put: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("PUT", path, { ...opts, json }),
  patch: <T = unknown>(path: string, json?: unknown, opts?: ApiOptions) =>
    doFetch<T>("PATCH", path, { ...opts, json }),
  del: <T = unknown>(path: string, opts?: ApiOptions) => doFetch<T>("DELETE", path, opts),
};
